import type { PortMap, PointPort } from '../../../port';
import type { SolvedCircle } from '../../../analytics';
import type { PlacedShape } from '../../../placement';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { resolveGeometryStyle } from '../../coloring';
import { initShapeCreatorContext } from '../shape-creator.util';
import type { CircleElement } from './circle.model';

/**
 * Create a circle element on the board.
 * Uses the base elementFactory.circle() for rendering.
 */
export function createCircle(
  element: CircleElement & { id: string },
  placed: PlacedShape,
  solved: SolvedCircle,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, elementDisplay, jsxObjects, labels, styleCtx } = initShapeCreatorContext(element, context);
  const center = placed.vertices['center'] as [number, number];

  const circleStyle = resolveGeometryStyle('circle', 'Circle', styleCtx, elementDisplay);
  const circleResult = elementFactory.circle({
    type: 'centerRadius',
    center,
    radius: solved.radius,
    style: {
      strokeColor: circleStyle.strokeColor,
      fillColor: circleStyle.fillColor,
      fillOpacity: circleStyle.fillOpacity,
      strokeWidth: circleStyle.strokeWidth,
      visible: elementDisplay?.visible ?? true,
      label: false,
    },
  });

  // Lock down circle and center by default (interactivity is opt-in)
  const typedCircle = circleResult.circle as unknown as { setAttribute: (a: Record<string, unknown>) => void };
  const typedCenter = circleResult.center as unknown as { setAttribute: (a: Record<string, unknown>) => void };
  typedCircle.setAttribute({ fixed: true, highlight: false });
  typedCenter.setAttribute({ fixed: true, highlight: false });

  jsxObjects.push(circleResult.circle, circleResult.center);
  jsxObjects.push(...circleResult.helpers);
  labels.push(circleResult.label);

  const centerPoint = circleResult.center as unknown as { X: () => number; Y: () => number };

  const ports: PortMap = {
    center: () => [centerPoint.X(), centerPoint.Y()] as PointPort,
    radius: () => solved.radius,
    area: () => Math.PI * solved.radius * solved.radius,
    circumference: () => 2 * Math.PI * solved.radius,
    // Expose the raw JSXGraph circle element for glider targets
    circle: () => circleResult.circle,
  };

  return {
    jsxObjects,
    labels,
    ports,
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}
